import { useMemo, useState } from "react";
import MoneyInput from "../../../../components/form/MoneyInput";
import { useBankTransfers } from "../../../../hooks/useBankTransfers";
import { useCustomers } from "../../../../hooks/useCustomers";
import { useSharePresales } from "../../../../hooks/useSharePresales";
import { createSharePresaleOperationRequest, updateSharePresaleOperationRequest } from "../../../../lib/api";
import { isBankTransferActive } from "../../../../lib/bankTransferStore";
import { formatTry, parseMoneyInput } from "../../../../lib/money";
import { applyOperationResult } from "../../../../lib/operationRefresh";
import type { SharePresalePaymentType, SharePresaleRecord } from "../../../../lib/sharePresalesStore";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function normalizeText(value?: string) {
  return (value ?? "").toLocaleLowerCase("tr-TR").replace(/\s+/g, " ").trim();
}

function formatDate(value?: string) {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function dateInRange(value: string | undefined, from: string, to: string) {
  const date = String(value ?? "").slice(0, 10);
  if (!date) return !from && !to;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

function openPresalePrintWindow(payload: {
  tarih: string;
  musteriAdi: string;
  telefon: string;
  paket: string;
  hayvanTipi: string;
  adet: number;
  birimTutar: number;
  toplamTutar: number;
  kaporaTutar: number;
  kalanTutar: number;
  odemeTipi: string;
  havaleNo?: string;
  not?: string;
  tasima?: boolean;
}) {
  const win = window.open("", "_blank", "width=760,height=900");
  if (!win) return;
  win.document.write(`<!doctype html><html><head><title>Hisse Ön Satış</title><style>
    body{font-family:Arial,sans-serif;margin:24px;color:#3f2a1f} .card{border:1px solid #d9c8b6;border-radius:14px;padding:18px} h1{font-size:22px;margin:0 0 4px}.muted{color:#7a6a5d;font-size:12px}.row{display:flex;justify-content:space-between;border-bottom:1px solid #eee;padding:8px 0;font-size:14px}.total{font-size:18px;font-weight:700}.green{color:#15803d}.orange{color:#b45309}@media print{button{display:none}}
  </style></head><body><div class="card"><h1>Evren Çiftliği - Hisse Ön Satış</h1><p class="muted">${formatDate(payload.tarih)}</p>
  <div class="row"><b>Müşteri</b><span>${payload.musteriAdi}</span></div>
  <div class="row"><b>Telefon</b><span>${payload.telefon}</span></div>
  <div class="row"><b>Paket</b><span>${payload.paket} / ${payload.hayvanTipi}</span></div>
  <div class="row"><b>Paket Tutarı</b><span>${formatTry(payload.birimTutar)}</span></div>
  <div class="row"><b>Adet</b><span>${payload.adet}</span></div>
  <div class="row total"><b>Toplam</b><span>${formatTry(payload.toplamTutar)}</span></div>
  <div class="row green"><b>Kapora</b><span>${formatTry(payload.kaporaTutar)} (${payload.odemeTipi})</span></div>
  <div class="row orange"><b>Kalan</b><span>${formatTry(payload.kalanTutar)}</span></div>
  ${payload.havaleNo ? `<div class="row"><b>Havale/Dekont</b><span>${payload.havaleNo}</span></div>` : ""}
  <div class="row"><b>Taşıma</b><span>${payload.tasima ? "Var" : "Yok"}</span></div>
  ${payload.not ? `<p class="muted"><b>Not:</b> ${payload.not}</p>` : ""}
  <p class="muted">Bu belge ön hisse satış bilgilendirmesidir. Hayvan/küpe bağlantısı operasyon planlamasında ayrıca yapılır.</p>
  <button onclick="window.print()">Yazdır</button></div></body></html>`);
  win.document.close();
}

type Props = {
  onClose: () => void;
  editingSale?: SharePresaleRecord | null;
};

export default function HisseSatisEkleDrawer({ onClose, editingSale = null }: Props) {
  const { customers } = useCustomers();
  const { transfers } = useBankTransfers();
  const { sharePresales } = useSharePresales();
  const isEdit = Boolean(editingSale);

  const [saleDate, setSaleDate] = useState(editingSale?.tarih ?? today());
  const [selectedCustomerId, setSelectedCustomerId] = useState(editingSale?.musteriId ? String(editingSale.musteriId) : "");
  const [customerName, setCustomerName] = useState(editingSale?.musteriAdi ?? "");
  const [customerPhone, setCustomerPhone] = useState(editingSale?.telefon ?? "");
  const [selectedPackageId, setSelectedPackageId] = useState(editingSale?.paketId ? String(editingSale.paketId) : "");
  const [quantity, setQuantity] = useState(String(editingSale?.adet ?? 1));
  const [depositAmount, setDepositAmount] = useState(editingSale?.kaporaTutar ? String(editingSale.kaporaTutar) : "");
  const [paymentType, setPaymentType] = useState<SharePresalePaymentType>((editingSale?.odemeTipi ?? "Nakit") as SharePresalePaymentType);
  const [havaleNo, setHavaleNo] = useState(editingSale?.havaleNo ?? "");
  const [saleNote, setSaleNote] = useState(editingSale?.not ?? "");
  const [hasTransport, setHasTransport] = useState(Boolean(editingSale?.tasima));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [printAfterSave, setPrintAfterSave] = useState(false);

  const [transferPickerOpen, setTransferPickerOpen] = useState(false);
  const [selectedTransferIds, setSelectedTransferIds] = useState<string[]>(editingSale?.linkedBankTransferIds ?? []);
  const [transferDateFrom, setTransferDateFrom] = useState("");
  const [transferDateTo, setTransferDateTo] = useState("");
  const [transferDescriptionFilter, setTransferDescriptionFilter] = useState("");

  const linkedCount = editingSale?.linkedShareIds?.length ?? 0;

  const activePackages = useMemo(() => {
    const list = [...sharePresales.packages]
      .filter((item) => item.aktif || (editingSale?.paketId && item.id === editingSale.paketId))
      .sort((a, b) => a.sıraNo - b.sıraNo || a.id - b.id);
    return list;
  }, [editingSale?.paketId, sharePresales.packages]);

  const selectedPackage = useMemo(
    () => sharePresales.packages.find((item) => String(item.id) === selectedPackageId) ?? null,
    [selectedPackageId, sharePresales.packages]
  );

  const activeBankTransfers = useMemo(() => {
    const q = normalizeText(transferDescriptionFilter);
    const owned = new Set(editingSale?.linkedBankTransferIds ?? []);
    return transfers
      .filter((item) => isBankTransferActive(item) || owned.has(String(item.id)))
      .filter((item) => dateInRange(item.tarih, transferDateFrom, transferDateTo))
      .filter((item) => !q || [item.fisNo, item.aciklama, item.manualNote ?? ""].some((value) => normalizeText(value).includes(q)))
      .sort((a, b) => b.tarih.localeCompare(a.tarih) || Number(b.tutar || 0) - Number(a.tutar || 0));
  }, [editingSale?.linkedBankTransferIds, transferDateFrom, transferDateTo, transferDescriptionFilter, transfers]);

  const saleQuantity = Math.max(1, Number(quantity) || 1);
  const unitAmount = selectedPackage?.tutar ?? editingSale?.birimTutar ?? 0;
  const totalAmount = saleQuantity * unitAmount;
  const paidAmount = Math.max(0, parseMoneyInput(depositAmount));
  const remainingAmount = Math.max(0, totalAmount - paidAmount);

  function handleCustomerSelect(value: string) {
    setSelectedCustomerId(value);
    const customer = customers.find((item) => String(item.id) === value);
    if (!customer) return;
    setCustomerName(customer.adSoyad);
    setCustomerPhone(customer.telefon);
  }

  function toggleTransferSelection(id: string) {
    setSelectedTransferIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  }

  function addSelectedTransfersToSale() {
    const selected = activeBankTransfers.filter((item) => selectedTransferIds.includes(item.id));
    if (selected.length === 0) return setError("Bağlamak için en az bir aktif havale seç.");
    const totalCents = selected.reduce((sum, item) => sum + Math.round(Number(item.tutar || 0) * 100), 0);
    const total = Math.round(totalCents / 100);
    setPaymentType("Havale");
    setDepositAmount(String(total));
    setHavaleNo(selected.map((item) => item.fisNo).join(", "));
    setTransferPickerOpen(false);
    setError("");
  }

  async function save() {
    if (!selectedPackage) return setError("Hisse tanımı seç.");
    if (!customerName.trim()) return setError("Müşteri adı gir veya müşteriden seç.");
    if (!customerPhone.trim()) return setError("Telefon gir.");
    if (paidAmount > totalAmount) return setError("Kapora toplam tutardan büyük olamaz.");
    if (linkedCount > 0 && saleQuantity !== linkedCount) return setError("Hayvana bağlı kayıtta adet değiştirilemez. Önce Çöz yapıp sonra düzenleyebilirsin.");
    if (paidAmount > 0 && paymentType === "Havale" && !havaleNo.trim() && selectedTransferIds.length === 0) return setError("Havale kapora için dekont/fiş no gir veya aktif havale bağla.");

    setSaving(true);
    setError("");
    try {
      const selectedCustomer = customers.find((item) => String(item.id) === selectedCustomerId);
      const payload = {
        saleId: editingSale?.id,
        tarih: saleDate || today(),
        customerId: selectedCustomer?.id ?? 0,
        musteriAdi: customerName.trim(),
        telefon: customerPhone.trim(),
        packageId: selectedPackage.id,
        adet: saleQuantity,
        kaporaTutar: paidAmount,
        odemeTipi: paymentType,
        havaleNo: paymentType === "Havale" ? havaleNo.trim() : "",
        linkedBankTransferIds: paymentType === "Havale" ? selectedTransferIds : [],
        tasima: hasTransport,
        not: saleNote.trim(),
      };

      if (isEdit) {
        await updateSharePresaleOperationRequest(payload);
      } else {
        await createSharePresaleOperationRequest(payload);
      }

      await applyOperationResult();
      if (printAfterSave) {
        openPresalePrintWindow({
          tarih: saleDate || today(),
          musteriAdi: customerName.trim(),
          telefon: customerPhone.trim(),
          paket: selectedPackage.baslik,
          hayvanTipi: selectedPackage.hayvanTipi,
          adet: saleQuantity,
          birimTutar: selectedPackage.tutar,
          toplamTutar: totalAmount,
          kaporaTutar: paidAmount,
          kalanTutar: remainingAmount,
          odemeTipi: paymentType,
          havaleNo: paymentType === "Havale" ? havaleNo.trim() : undefined,
          tasima: hasTransport,
          not: saleNote.trim() || undefined,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Hisse ön satışı kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-end bg-black/40 px-0 sm:px-4">
      <div className="h-[94vh] w-full max-w-2xl overflow-hidden rounded-t-farm border border-farm-border bg-farm-warm shadow-xl sm:my-4 sm:rounded-farm">
        <div className="flex items-center justify-between border-b border-farm-border bg-white px-4 py-3">
          <div>
            <p className="font-heading text-lg font-bold text-farm-brown">{isEdit ? "Hisse Satışı Düzenle" : "Hisse Satışı Ekle"}</p>
            <p className="font-body text-[11px] text-farm-muted">Hayvandan bağımsız ön hisse satışı. Hayvana bağlantı daha sonra yapılır.</p>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full text-farm-muted hover:bg-farm-cream"><i className="ri-close-line" /></button>
        </div>

        <div className="h-[calc(94vh-128px)] space-y-3 overflow-y-auto px-4 py-4">
          {linkedCount > 0 && (
            <div className="rounded-farm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
              Bu kayıt hayvana bağlı. Müşteri, kapora, ödeme tipi ve not düzenlenebilir. Adet değişikliği için önce Çöz yapılmalı.
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Tarih</span><input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none" /></label>
            <label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Adet</span><input type="number" min={1} value={quantity} disabled={linkedCount > 0} onChange={(e) => setQuantity(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none disabled:bg-farm-cream disabled:text-farm-muted" /></label>
            <label className="block sm:col-span-2"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Mevcut Müşteriden Seç</span><select value={selectedCustomerId} onChange={(e) => handleCustomerSelect(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none"><option value="">Müşteri seçmeden elle yaz</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.adSoyad} - {customer.telefon}</option>)}</select></label>
            <label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Müşteri Adı</span><input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none" /></label>
            <label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Telefon</span><input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none" /></label>
            <label className="block sm:col-span-2"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Hisse Tanımı</span><select value={selectedPackageId} disabled={linkedCount > 0} onChange={(e) => setSelectedPackageId(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none disabled:bg-farm-cream disabled:text-farm-muted"><option value="">Tanım seç</option>{activePackages.map((item) => <option key={item.id} value={item.id}>{item.sıraNo}. {item.baslik} - {item.hayvanTipi} - {formatTry(item.tutar)}</option>)}</select></label>
          </div>

          <div className="rounded-farm border border-farm-border bg-white/70 p-3">
            <p className="mb-2 text-xs font-bold text-farm-brown">Kapora / Ödeme</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Kapora Tutarı</span><MoneyInput value={depositAmount} onChange={setDepositAmount} className="bg-white" /></label>
              <label><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Kalan</span><div className="flex h-10 items-center rounded-farm border border-farm-border bg-farm-cream px-3 text-xs font-bold text-farm-brown">{formatTry(remainingAmount)}</div></label>
              <div><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Kapora Tipi</span><div className="grid grid-cols-2 gap-2">{(["Nakit", "Havale"] as const).map((type) => <button key={type} type="button" onClick={() => setPaymentType(type)} className={`h-10 rounded-farm border px-3 text-xs font-bold ${paymentType === type ? "border-farm-earth bg-farm-earth text-white" : "border-farm-border bg-white text-farm-brown"}`}>{type}</button>)}</div></div>
              <label><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Havale / Dekont No</span><input value={havaleNo} onChange={(e) => setHavaleNo(e.target.value)} className="h-10 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none" /></label>
            </div>
            {paymentType === "Havale" && <button type="button" onClick={() => setTransferPickerOpen(true)} className="mt-3 rounded-full bg-sky-600 px-4 py-2 text-xs font-bold text-white hover:bg-sky-700">Aktif Havale Bağla</button>}
          </div>

          <label className="flex items-center justify-between rounded-farm border border-farm-border bg-white px-3 py-3">
            <span>
              <span className="block text-xs font-bold text-farm-brown">Taşıma</span>
              <span className="block text-[10px] text-farm-muted">Bu hisse ön satışında taşıma isteği var mı?</span>
            </span>
            <input
              type="checkbox"
              checked={hasTransport}
              onChange={(e) => setHasTransport(e.target.checked)}
              className="h-5 w-5 accent-farm-earth"
            />
          </label>

          <label className="block"><span className="mb-1 block text-[10px] font-semibold uppercase text-farm-muted">Not</span><textarea value={saleNote} onChange={(e) => setSaleNote(e.target.value)} rows={3} className="w-full rounded-farm border border-farm-border bg-white px-3 py-2 text-xs outline-none" /></label>

          <div className="rounded-farm border border-farm-border bg-white px-3 py-3">
            <div className="grid grid-cols-3 gap-2 text-center text-xs"><div><p className="text-[10px] text-farm-muted">Birim</p><b>{formatTry(unitAmount)}</b></div><div><p className="text-[10px] text-farm-muted">Toplam</p><b>{formatTry(totalAmount)}</b></div><div><p className="text-[10px] text-farm-muted">Kapora</p><b className="text-farm-green">{formatTry(paidAmount)}</b></div></div>
          </div>

          {error && <div className="rounded-farm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">{error}</div>}
        </div>

        <div className="flex flex-col gap-2 border-t border-farm-border bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-2 text-xs font-semibold text-farm-muted"><input type="checkbox" checked={printAfterSave} onChange={(e) => setPrintAfterSave(e.target.checked)} /> Kaydedince yazdır</label>
          <div className="flex gap-2"><button type="button" onClick={onClose} className="h-10 rounded-full border border-farm-border px-5 text-xs font-semibold text-farm-muted">İptal</button><button type="button" onClick={save} disabled={saving} className="h-10 rounded-full bg-farm-earth px-5 text-xs font-bold text-white disabled:opacity-60">{isEdit ? "Güncelle" : "Kaydet"}</button></div>
        </div>
      </div>

      {transferPickerOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-4xl rounded-xl bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3"><div><h4 className="text-sm font-bold text-gray-800">Aktif Havale Bağla</h4><p className="text-[10px] text-gray-500">Boşta duran aktif havaleleri kaporaya bağlar. Düzenlemede mevcut bağlı havale de listede görünebilir.</p></div><button type="button" onClick={() => setTransferPickerOpen(false)} className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100"><i className="ri-close-line" /></button></div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3"><input type="date" value={transferDateFrom} onChange={(event) => setTransferDateFrom(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" /><input type="date" value={transferDateTo} onChange={(event) => setTransferDateTo(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" /><input value={transferDescriptionFilter} onChange={(event) => setTransferDescriptionFilter(event.target.value)} placeholder="Açıklama / Fiş No içinde ara" className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" /></div>
            <div className="mt-3 max-h-[360px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">{activeBankTransfers.length === 0 ? <div className="px-4 py-8 text-center text-xs text-gray-500">Aktif havale yok.</div> : activeBankTransfers.map((item) => { const checked = selectedTransferIds.includes(item.id); return <button key={item.id} type="button" onClick={() => toggleTransferSelection(item.id)} className={`grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 px-3 py-2.5 text-left text-xs hover:bg-sky-50 ${checked ? "bg-sky-50" : ""}`}><span className="pt-0.5"><span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? "border-sky-600 bg-sky-600 text-white" : "border-gray-300 bg-white"}`}>{checked && <i className="ri-check-line text-xs" />}</span></span><span className="font-semibold text-gray-500">{formatDate(item.tarih)}</span><span className="font-bold text-sky-700">{item.fisNo}</span><span className="whitespace-normal break-words leading-snug text-gray-700">{item.aciklama || "—"}</span><span className="text-right font-bold text-gray-800">{formatTry(Number(item.tutar || 0))}</span></button>; })}</div>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3"><p className="text-[10px] text-gray-500">{selectedTransferIds.length} havale seçildi. Seçili tutar kapora alanına aktarılır.</p><button type="button" onClick={addSelectedTransfersToSale} disabled={selectedTransferIds.length === 0} className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:opacity-40">Seçili Havaleleri Bağla</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
